import React, { useState, useEffect, useRef } from "react";
import useAxios from "../../hooks/useAxios";
import { useAuth } from "../../contexts/AuthContext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { FilterMatchMode } from "primereact/api";
import { useNavigate } from "react-router-dom";

const Users = () => {
  const { user } = useAuth();
  const { response, loading, fetchData } = useAxios();
  const { response: editResponse, fetchData: fetchEditData } = useAxios();
  const { response: deleteResponse, fetchData: fetchDeleteData } = useAxios();
  const navigate = useNavigate();
  const toast = useRef(null);

  const [users, setUsers] = useState([]);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    fetchData({ method: "get", url: "/api/users" });
  }, []);

  useEffect(() => {
    if (!response) return;
    if (response.status === "success" && response.data) {
      setUsers(response.data);
    }
  }, [response]);

  useEffect(() => {
    if (!editResponse) return;
    if (editResponse.status === "success") {
      toast.current.show({ severity: "success", detail: editResponse.message, life: 3000 });
      setEditDialogVisible(false);
      fetchData({ method: "get", url: "/api/users" });
    } else {
      toast.current.show({ severity: "error", detail: editResponse.message || "Ошибка", life: 3000 });
    }
  }, [editResponse]);

  useEffect(() => {
    if (!deleteResponse) return;
    if (deleteResponse.status === "success") {
      toast.current.show({ severity: "success", detail: deleteResponse.message, life: 3000 });
      fetchData({ method: "get", url: "/api/users" });
    } else {
      toast.current.show({ severity: "error", detail: deleteResponse.message || "Ошибка", life: 3000 });
    }
  }, [deleteResponse]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const openEditDialog = (rowData) => {
    setEditForm({
      _id: rowData._id,
      email: rowData.email || "",
      firstname: rowData.firstname || "",
      lastname: rowData.lastname || "",
      mobile: rowData.mobile || "",
      isAdmin: rowData.isAdmin || false,
      roles: rowData.roles || [],
      password: "",
    });
    setEditDialogVisible(true);
  };

  const handleEditSave = () => {
    const { _id, ...data } = editForm;
    if (!data.password) delete data.password;
    fetchEditData({ method: "put", url: `/api/users/${_id}`, data });
  };

  const handleDelete = (rowData) => {
    confirmDialog({
      message: `Удалить пользователя ${rowData.email}?`,
      header: "Подтверждение",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Удалить",
      rejectLabel: "Отмена",
      acceptClassName: "p-button-danger",
      accept: () => {
        fetchDeleteData({ method: "delete", url: `/api/users/${rowData._id}` });
      },
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const rolesBodyTemplate = (rowData) => {
    return (
      <div className="flex flex-wrap gap-1">
        {rowData.roles?.map((role, i) => (
          <Tag key={i} value={role} severity="info" />
        ))}
      </div>
    );
  };

  const adminBodyTemplate = (rowData) => {
    return rowData.isAdmin ? (
      <Tag value="Админ" severity="success" />
    ) : null;
  };

  const lastLoginBodyTemplate = (rowData) => {
    return formatDate(rowData.lastLogin);
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={() => openEditDialog(rowData)}
          tooltip="Редактировать"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger p-button-sm"
          onClick={() => handleDelete(rowData)}
          tooltip="Удалить"
        />
      </div>
    );
  };

  if (!user?.isAdmin) {
    return <div className="p-5 text-center text-red-500 font-bold">Доступ только для администраторов</div>;
  }

  return (
    <div className="p-4">
      <Toast ref={toast} position="top-right" />
      <ConfirmDialog />

      <div className="flex align-items-center justify-content-between mb-4">
        <h1 className="text-3xl font-bold m-0">Пользователи системы</h1>
        <div className="flex gap-2">
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Поиск..."
            className="p-inputtext-sm"
          />
          <Button
            label="Создать"
            icon="pi pi-plus"
            className="p-button-sm"
            onClick={() => navigate("/signup")}
          />
        </div>
      </div>

      {loading ? (
        <ProgressBar mode="indeterminate" style={{ height: "6px" }} />
      ) : (
        <DataTable
          header="Список учетных записей"
          value={users}
          filters={filters}
          globalFilterFields={["email", "firstname", "lastname", "mobile"]}
          size="small"
          showGridlines
          stripedRows
          emptyMessage="Нет пользователей"
          sortField="createdAt"
          sortOrder={-1}
          style={{ fontSize: "0.9rem" }}
          className="compact-table"
        >
          <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: "50px", textAlign: "center" }} />
          <Column field="email" header="Email" sortable style={{ minWidth: "180px" }} />
          <Column field="firstname" header="Имя" sortable style={{ minWidth: "120px" }} />
          <Column field="lastname" header="Фамилия" sortable style={{ minWidth: "120px" }} />
          <Column field="mobile" header="Телефон" style={{ minWidth: "120px" }} />
          <Column header="Роли" body={rolesBodyTemplate} style={{ minWidth: "150px" }} />
          <Column header="Админ" body={adminBodyTemplate} style={{ width: "80px" }} />
          <Column header="Последний вход" body={lastLoginBodyTemplate} sortable sortField="lastLogin" style={{ minWidth: "150px" }} />
          <Column header="" body={actionsBodyTemplate} style={{ width: "100px" }} />
        </DataTable>
      )}

      {/* Edit Dialog */}
      <Dialog
        visible={editDialogVisible}
        onHide={() => setEditDialogVisible(false)}
        header="Редактирование пользователя"
        style={{ width: "500px" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Отмена" icon="pi pi-times" className="p-button-text p-button-sm" onClick={() => setEditDialogVisible(false)} />
            <Button label="Сохранить" icon="pi pi-check" className="p-button-sm" onClick={handleEditSave} />
          </div>
        }
      >
        <div className="grid">
          <div className="col-12">
            <label className="block font-medium mb-1">Email</label>
            <InputText
              value={editForm.email}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div className="col-6">
            <label className="block font-medium mb-1">Имя</label>
            <InputText
              value={editForm.firstname}
              onChange={(e) => setEditForm({ ...editForm, firstname: e.target.value })}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div className="col-6">
            <label className="block font-medium mb-1">Фамилия</label>
            <InputText
              value={editForm.lastname}
              onChange={(e) => setEditForm({ ...editForm, lastname: e.target.value })}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div className="col-6">
            <label className="block font-medium mb-1">Телефон</label>
            <InputText
              value={editForm.mobile}
              onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div className="col-6">
            <label className="block font-medium mb-1">Новый пароль</label>
            <Password
              value={editForm.password}
              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              className="w-full"
              inputClassName="w-full p-inputtext-sm"
              toggleMask
              feedback={false}
              placeholder="Оставьте пустым"
            />
          </div>
          <div className="col-12">
            <label className="block font-medium mb-1">Роли</label>
            <Chips
              value={editForm.roles}
              onChange={(e) => setEditForm({ ...editForm, roles: e.value })}
              className="w-full"
              placeholder="Enter для добавления"
            />
          </div>
          <div className="col-12">
            <div className="field-checkbox">
              <Checkbox
                inputId="editIsAdmin"
                checked={editForm.isAdmin}
                onChange={(e) => setEditForm({ ...editForm, isAdmin: e.checked })}
              />
              <label htmlFor="editIsAdmin" className="ml-2">Администратор</label>
            </div>
          </div>
        </div>
      </Dialog>

      <style>{`
        .compact-table .p-datatable-tbody > tr > td {
          padding: 0.25rem 0.5rem;
        }
        .compact-table .p-datatable-thead > tr > th {
          padding: 0.4rem 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default Users;
